import React, { useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { GetServerSideProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

import { getBrands } from "api/requests/getBrands";
import { getUserLocationData } from "api/requests/getUserLocation";
import { Brand } from "custom-types/Brand";
import useDomain from "hooks/useDomain";
import { getFirstQueryParam } from "utils/nextRouterUtils";
import { getCountryCodeHeader } from "utils/requestContext";

import Tab from "components/botanic/Tab";
import Tabs from "components/botanic/Tabs";
import AllBrandsEmptyState from "components/brands/AllBrands/AllBrandsEmptyState";
import BrandCoins from "components/brands/BrandCoins";
import BrandsNearYouEmptyState from "components/brands/BrandsNearYou/BrandsNearYouEmptyState";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import SearchIcon from "components/Icons/searchBar/search.svg";
import CloseIcon from "components/Icons/searchBar/x.svg";
import PageNavigation from "components/PageNavigation";

type BrandIndexPageProps = {
  initialBrands: Array<Brand>;
  totalCount: number;
  hasAvailableNearby: boolean;
  isAllBrandsPage?: boolean;
};

const PER_PAGE = 60;

enum LocationFilter {
  ALL = "all",
  NEARBY = "nearby",
}

const BrandIndexPage: NextPage<BrandIndexPageProps> = ({
  initialBrands,
  totalCount,
  hasAvailableNearby,
  isAllBrandsPage = false,
}) => {
  const router = useRouter();
  const domain = useDomain();
  const [searchTerm, setSearchTerm] = useState(router.query.search || "");
  const pageCount = Math.ceil(totalCount / PER_PAGE);
  const currentPage = Number(router.query.page || 1);

  const getURLForTab = (location: LocationFilter) => {
    return `/brands${location === LocationFilter.ALL ? "/all" : ""}${
      searchTerm ? `?search=${searchTerm}` : ""
    }`;
  };

  const nextPage = async () => {
    router.push({
      query: { ...router.query, page: currentPage + 1 },
    });
  };

  const prevPage = async () => {
    router.push({
      query: { ...router.query, page: currentPage - 1 },
    });
  };

  const onPageSelect = async (page: number) => {
    router.push({
      query: { ...router.query, page },
    });
  };

  const search = debounce((event) => {
    // this will stop ?search= from being added to the query params
    if (!event.target.value && !searchTerm) {
      return;
    }
    router.push({
      query: {
        // This will add search to the query and trigger and search is there is a search value present
        // OR it will clear the search and return the default result set.
        ...(event.target.value ? { search: event.target.value } : {}),
      },
    });
  }, 300);

  const H1Text = useMemo(() => {
    if (searchTerm) {
      return `Showing results for ${searchTerm}`;
    }
    if (isAllBrandsPage) {
      return "All weed and CBD brands";
    }
    return "Weed and CBD brands near you";
  }, [searchTerm, isAllBrandsPage]);

  const currentPathForMeta = useMemo(() => {
    const { query, pathname } = router;

    const { page } = query;

    const queryToUse = Number(page) > 1 ? query : "";

    const queryString = new URLSearchParams(
      queryToUse as Record<string, string>,
    ).toString();

    if (!queryString) {
      return pathname;
    }

    return pathname + "?" + queryString;
  }, [router.asPath]);

  return (
    <>
      <Head>
        <title>
          {isAllBrandsPage
            ? "All Weed and CBD Brands on Leafly"
            : "Shop Weed and CBD Brands Near You"}
        </title>
        <meta
          name="description"
          content={
            isAllBrandsPage
              ? "Find more information about established and emerging weed brands, CDB brands, cannabis growers, cart makers, and more on Leafly."
              : "Explore, find, and learn more about weed farms, weed pen brands, cbd brands, and other makers of the best cannabis products near you."
          }
        />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={`${domain}${currentPathForMeta}`} />
        <link
          href={`https://www.leafly.com${currentPathForMeta}`}
          hrefLang="en-us"
          rel="alternate"
        />
        <link
          href={`https://www.leafly.ca${currentPathForMeta}`}
          hrefLang="en-ca"
          rel="alternate"
        />
      </Head>
      <Breadcrumbs>
        <Breadcrumb href="/brands" currentLocation>
          Brands
        </Breadcrumb>
      </Breadcrumbs>
      <div className="container">
        <h1 className="mb-md">{H1Text}</h1>
        <div className="relative mb-lg">
          <div className="absolute top-0 left-1.5 flex h-full text-default">
            <button
              id="global-search__submit"
              className="px-lg"
              type="submit"
              aria-label="Search Leafly"
            >
              <SearchIcon
                className="flex-shrink-0 block search__icon"
                height="16"
                width="16"
              />
            </button>
          </div>
          <input
            id="searchbox"
            className="pl-10 p-sm bg-white"
            aria-label="Brand name search"
            data-testid="search-input"
            placeholder="Search brands"
            type="text"
            value={searchTerm}
            defaultValue={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              search(e);
            }}
          />
          {searchTerm && (
            <div className="absolute top-0 right-0 flex h-full text-default">
              <button
                className="px-sm"
                type="button"
                aria-label="Clear search"
                data-testid="clear-search-icon"
                onClick={() => {
                  setSearchTerm("");
                  router.push({});
                }}
              >
                <CloseIcon
                  className="flex-shrink-0 block search__icon"
                  height="15"
                  width="15"
                />
              </button>
            </div>
          )}
        </div>
        <Tabs className="mb-xl" ariaLabel="Brands page">
          <Tab
            active={!isAllBrandsPage}
            href={getURLForTab(LocationFilter.NEARBY)}
          >
            Available nearby
          </Tab>
          <Tab active={isAllBrandsPage} href={getURLForTab(LocationFilter.ALL)}>
            All brands
          </Tab>
        </Tabs>
        {initialBrands.length === 0 &&
          !isAllBrandsPage &&
          !hasAvailableNearby && (
            <div className="mt-6 mb-8">
              <BrandsNearYouEmptyState />
            </div>
          )}
        {initialBrands.length === 0 && isAllBrandsPage && (
          <div className="mt-6 mb-8">
            <AllBrandsEmptyState
              onButtonClick={() => {
                setSearchTerm("");
                router.push({});
              }}
            />
          </div>
        )}
        {(initialBrands.length > 0 || isAllBrandsPage) && (
          <>
            <BrandCoins initialBrands={initialBrands} />
            <PageNavigation
              currentPage={currentPage}
              onNext={nextPage}
              onPrev={prevPage}
              onPageSelect={onPageSelect}
              totalPages={pageCount}
              className=" mt-8"
            />
            <div className="text-center mt-8 mb-section">
              {isAllBrandsPage && (
                <>
                  Find information about all of the different weed farms, indoor
                  cannabis growers, weed pen brands, CBD gummy makers, and more.
                  Use Leafly to find a comprehensive list of weed and CBD brands
                  and manufacturers operating in the US and Canada.
                </>
              )}
              {!isAllBrandsPage && (
                <>
                  Browse the best and emerging cannabis growers, weed pen and
                  cart makers, CBD brands, and more on Leafly. Find{" "}
                  <Link href="/dispensaries" className="underline font-bold">
                    dispensaries
                  </Link>{" "}
                  that carry weed products from your favorite top-shelf weed
                  brands while discovering{" "}
                  <Link href="/deals" className="underline font-bold">
                    cannabis and CBD deals
                  </Link>{" "}
                  available near you.
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const countryCode = getCountryCodeHeader(context.req.headers);
  const currentPage = Number(context.query.page || 1);

  const isAllBrandsPage = context.resolvedUrl?.includes("/all");

  const searchTerm = getFirstQueryParam(context.query.search);
  const toSkip = (currentPage - 1) * PER_PAGE;

  const sortKey = `sort[][${isAllBrandsPage ? "name" : "recommended"}]`;
  const searchOrSort = searchTerm
    ? { searchTerm }
    : { sort: { [sortKey]: "asc" as const } };

  if (isAllBrandsPage) {
    const allBrandsPageResponse = await getBrands({
      countryCode,
      skip: toSkip,
      take: PER_PAGE,
      ...searchOrSort,
    });

    return {
      props: {
        initialBrands: allBrandsPageResponse.data,
        isAllBrandsPage: true,
        totalCount: allBrandsPageResponse.metadata.totalCount,
      },
    };
  } else {
    const { coordinates } = await getUserLocationData(countryCode, context);

    const availableBrandsResponse = await getBrands({
      coordinates: {
        lat: coordinates?.lat || coordinates?.latitude,
        lon: coordinates?.lon || coordinates?.longitude || coordinates?.lng,
      },
      countryCode,
      skip: toSkip,
      take: PER_PAGE,
      ...searchOrSort,
    });

    const hasAvailableNearby = Boolean(
      availableBrandsResponse.metadata.totalCount,
    );

    return {
      props: {
        hasAvailableNearby,
        initialBrands: hasAvailableNearby ? availableBrandsResponse.data : [],
        isAllBrandsPage: false,
        totalCount: hasAvailableNearby
          ? availableBrandsResponse.metadata.totalCount
          : 0,
      },
    };
  }
};

export default BrandIndexPage;
